<template>
  <section class="action-log-section-component">
    <h3 v-if="!noTitle" class="version-title">
      Version {{ version.historySeq }} -
      <span class="version-name">{{ versionName }}</span>
    </h3>
    <VdCard small="true" class="logs-list">
      <template #header>
        <div
          v-for="entry in formattedLogs"
          :key="entry.id"
          class="log-entry"
          :class="[entry.action.toLowerCase(), { erased: entry.isErased }]"
        >
          <span class="entry-info">
            <template v-if="entry.action === ActionLogAction.Rollback">
              <span class="entity">{{ entry.author }}&nbsp;</span>
              <span class="action">rolled back</span>
              <span class="text">&nbsp;version&nbsp;</span>
              <span>
                <router-link :to="entry.links.entity.url">{{ entry.entity.name }}&nbsp;</router-link>
              </span>
              <span class="text">to WIP version. All previous work on WIP version was erased</span>
            </template>
            <template v-else>
              <span class="entity">{{ entry.author }}&nbsp;</span>
              <span class="action" :class="entry.action.toLowerCase()">{{ entry.action.toLowerCase() }}</span>
              <span v-if="entry.action === ActionLogAction.Created" class="text">
                a new
                <span class="entity">{{ entry.entity.type.toLowerCase() }}</span>
                named
              </span>
              <span
                v-if="entry.action === ActionLogAction.Updated || entry.action === ActionLogAction.Deleted"
                class="text entity"
              >
                the {{ entry.entity.type.toLowerCase() }}
              </span>

              <span>
                <router-link :to="entry.links.entity.url">{{ entry.entity.name }}</router-link>
                <span v-if="entry.links.metadata">
                  on
                  <router-link :to="entry.links.metadata.url">{{ entry.links.metadata.label }}</router-link>
                </span>
              </span>
            </template>
          </span>
          <span class="entry-date">
            {{ entry.timestamp }}
          </span>
        </div>
      </template>
    </VdCard>
  </section>
</template>

<script lang="ts">
import { VdCard } from "@voodoo.io/voodoo-ui";
import { parseISO, format } from "date-fns";
import { computed, defineComponent, PropType } from "vue";

import {
  ActionLogsListPageActionLogFragment,
  ActionLogAction,
  ActionLogEntityType,
  ActionLogMetadata,
  ActionLogsListPageVersionFragment,
  ActionLogsReviewVersionFragment,
  ActionLogsReviewActionLogFragment,
} from "@/graphql/generated-types";
import links from "@/router/links";

export default defineComponent({
  name: "ActionLogSection",

  components: {
    VdCard,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },

    version: {
      type: Object as PropType<ActionLogsListPageVersionFragment | ActionLogsReviewVersionFragment>,
      required: true,
    },

    noTitle: {
      type: Boolean,
      required: false,
      default: false,
    },

    logs: {
      type: Array as PropType<ActionLogsListPageActionLogFragment[] | ActionLogsReviewActionLogFragment[]>,
      required: true,
    },
  },
  setup(props) {
    const versionName = computed(() => (props.version.isWip ? "Working version" : props.version.name));

    const getEntityUrl = (type: ActionLogEntityType, id?: string | null, metadata?: ActionLogMetadata | null) => {
      const urlParams = {
        gameId: props.gameId,
        versionId: props.version.humanId,
      };

      if (type === ActionLogEntityType.Configuration) {
        return {
          name: links.games.configurations.previewRule,
          params: {
            ...urlParams,
            classId: metadata?.id,
            ruleId: id,
          },
        };
      }

      if (type === ActionLogEntityType.Cohort) {
        return {
          name: links.games.abtests.cohorts.edit,
          params: {
            ...urlParams,
            abtestId: metadata?.id,
            cohortId: id,
          },
        };
      }

      const urlMappings = {
        [ActionLogEntityType.Class]: links.games.classes.preview,
        [ActionLogEntityType.Segment]: links.games.segments.preview,
        [ActionLogEntityType.Feature]: links.games.features.preview,
        [ActionLogEntityType.Abtest]: links.games.abtests.review,
        [ActionLogEntityType.Version]: links.games.home,
      };

      return {
        name: urlMappings[type],
        params: {
          ...urlParams,
          [`${type.toLowerCase()}Id`]: id,
        },
      };
    };

    const getMetadataLabel = (type: ActionLogEntityType, metadata?: ActionLogMetadata | null): string => {
      const labelMapping = {
        [ActionLogEntityType.Configuration]: metadata?.name ?? "",
        [ActionLogEntityType.Class]: "",
        [ActionLogEntityType.Segment]: "",
        [ActionLogEntityType.Feature]: "",
        [ActionLogEntityType.Abtest]: "",
        [ActionLogEntityType.Version]: "",
        [ActionLogEntityType.Cohort]: metadata?.name ?? "",
      };

      return labelMapping[type];
    };

    const formattedLogs = computed(() => {
      const rollbackTimes = props.logs
        .filter((log) => log.action === ActionLogAction.Rollback)
        .map((log) => parseISO(log.timestamp).getTime());

      const latestRollbackTime = rollbackTimes.length > 0 ? Math.max(...rollbackTimes) : null;

      return props.logs.map((entry) => {
        const logTime = parseISO(entry.timestamp).getTime();
        const isErased = latestRollbackTime !== null && logTime < latestRollbackTime;

        const result = {
          ...entry,
          isErased,
          timestamp: format(parseISO(entry.timestamp), "PPPpp"),
          links: {
            entity: {
              label: entry.entity.name,
              url: getEntityUrl(entry.entity.type, entry.entity.id, entry.metadata),
            },
            metadata: null,
          },
        };

        const metadataTypeByEntityTypes: { [key: string]: ActionLogEntityType } = {
          [ActionLogEntityType.Configuration]: ActionLogEntityType.Class,
          [ActionLogEntityType.Cohort]: ActionLogEntityType.Abtest,
        };

        const metadataType = metadataTypeByEntityTypes[entry.entity.type];
        if (entry.metadata?.id) {
          return {
            ...result,
            links: {
              ...result.links,
              metadata: {
                label: getMetadataLabel(entry.entity.type, entry.metadata),
                url: getEntityUrl(metadataType, entry.metadata.id, entry.metadata),
              },
            },
          };
        }

        return result;
      });
    });

    return {
      formattedLogs,
      versionName,
      ActionLogAction,
      ActionLogEntityType,
    };
  },
});
</script>

<style lang="scss" scoped>
.action-log-section-component {
  .version-title {
    font-weight: 600;
    margin-top: size("ML");
    margin-bottom: size("ML");

    .version-name {
      font-size: font("M");
      color: $grey;
    }
  }

  .logs-list {
    .log-entry {
      display: grid;
      grid-template-columns: 22px 1fr auto;
      align-items: center;
      border-bottom: 1px solid var(--vd-card-white-border-color);
      height: 60px;
      box-sizing: border-box;
      padding: #{size("S") * 2} size("ML");

      &:last-of-type {
        border-bottom: 0;
      }

      &:before {
        content: "";
        display: inline-block;
        width: 2px;
        height: 100%;
        background-color: $green-alert;
      }

      &.deleted {
        .action {
          color: $red-alert;
        }

        &:before {
          background-color: $red-alert;
        }
      }

      &.updated {
        .action {
          color: $orange-alert;
        }

        &:before {
          background-color: $orange-alert;
        }
      }

      &.rollback {
        .action {
          color: $red-alert;
        }

        &:before {
          background-color: $red-alert;
        }
      }

      &.erased {
        opacity: 0.5;
        text-decoration: line-through;
      }

      .action {
        color: $green-alert;
      }

      .entity {
        font-weight: 600;
      }

      a {
        margin-left: 3px;
      }

      entry-date {
        justify-self: end;
      }
    }
  }
}
</style>
